'use client';

/**
 * Lifecycle helper for `FileDropZone` callers (LF-4642 / LF-4771).
 *
 * Presentational component + this hook = a working drop zone with real
 * progress, cancel and retry. The caller supplies only an `upload` transport
 * that composes its surface-specific pre-sign + upload-complete around the
 * shared `uploadFileToUrl` primitive (see `@/lib/uploads`); everything else —
 * validation, per-file state, progress plumbing, abort controllers — lives
 * here, so no upload/progress code is hand-rolled per surface.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { UploadFile } from './types';
import { validateFileAccept } from './validate-file';

/** What the hook hands the caller's `upload` transport for one file. */
export interface FileDropZoneUploadContext {
  /** Report upload progress, 0–100. */
  onProgress: (pct: number) => void;
  /**
   * Aborts when the row is cancelled (X), removed, cleared, or the zone
   * unmounts. Pass to `uploadFileToUrl({ signal })` so a cancel truly stops the
   * PUT; a resulting `AbortError` is swallowed (silent cancel, not an error row).
   */
  signal: AbortSignal;
}

interface UseFileDropZoneStateOptions {
  /** Native `<input accept>` string; used to reject unsupported drops. */
  accept?: string;
  /** Client-side size ceiling in bytes. */
  maxSizeBytes?: number;
  /** Single vs multiple. Default `true`. */
  multiple?: boolean;
  /**
   * The caller's upload transport. Report progress via `ctx.onProgress` and
   * honour `ctx.signal`; throw to fail the file. Omit to stage files as
   * `pending` only.
   */
  upload?: (file: File, ctx: FileDropZoneUploadContext) => Promise<void>;
}

interface UseFileDropZoneStateReturn {
  files: UploadFile[];
  onFilesAdded: (files: File[]) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  clear: () => void;
}

export function useFileDropZoneState({
  accept,
  maxSizeBytes,
  multiple = true,
  upload
}: UseFileDropZoneStateOptions = {}): UseFileDropZoneStateReturn {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const counterRef = useRef(0);
  const nextId = () => `fdz-${Date.now()}-${(counterRef.current += 1)}`;

  // One AbortController per in-flight upload, so a Cancel/Remove aborts the
  // real PUT (and a retry gets a fresh controller).
  const controllersRef = useRef(new Map<string, AbortController>());

  // Validate one file → an error message, or undefined if it's acceptable.
  // Shared by add and retry so a validation-error row can never be re-uploaded.
  const validate = useCallback(
    (file: File): string | undefined =>
      validateFileAccept(file, { accept, maxSizeBytes }),
    [accept, maxSizeBytes]
  );

  const runUpload = useCallback(
    async (id: string, file: File) => {
      if (!upload) return;

      const controller = new AbortController();
      controllersRef.current.set(id, controller);

      setFiles((cur) =>
        cur.map((f) =>
          f.id === id
            ? { ...f, status: 'uploading', progress: 0, error: undefined }
            : f
        )
      );

      try {
        await upload(file, {
          onProgress: (pct) => {
            const clamped = Math.min(Math.max(pct, 0), 100);
            setFiles((cur) =>
              cur.map((f) => (f.id === id ? { ...f, progress: clamped } : f))
            );
          },
          signal: controller.signal
        });
        // Cancelled while finishing → its row is already gone; don't resurrect it.
        if (controller.signal.aborted) return;
        setFiles((cur) =>
          cur.map((f) =>
            f.id === id ? { ...f, status: 'success', progress: 100 } : f
          )
        );
      } catch (err) {
        // A cancel (our own signal aborted) is silent — the row was already
        // removed by onRemove / clear / unmount.
        if (controller.signal.aborted) return;
        setFiles((cur) =>
          cur.map((f) =>
            f.id === id
              ? {
                  ...f,
                  status: 'error',
                  error: err instanceof Error ? err.message : 'Upload failed'
                }
              : f
          )
        );
      } finally {
        // Only drop our own controller — a retry may have installed a newer one.
        if (controllersRef.current.get(id) === controller) {
          controllersRef.current.delete(id);
        }
      }
    },
    [upload]
  );

  const onFilesAdded = useCallback(
    (incoming: File[]) => {
      const toAdd = multiple ? incoming : incoming.slice(0, 1);
      const created: UploadFile[] = toAdd.map((file) => {
        const error = validate(file);
        return {
          file,
          id: nextId(),
          progress: 0,
          status: error ? 'error' : 'pending',
          error
        };
      });

      setFiles((cur) => (multiple ? [...cur, ...created] : created));
      created
        .filter((f) => f.status === 'pending')
        .forEach((f) => void runUpload(f.id, f.file));
    },
    [multiple, validate, runUpload]
  );

  const onRemove = useCallback((id: string) => {
    const controller = controllersRef.current.get(id);
    if (controller) {
      controller.abort();
      controllersRef.current.delete(id);
    }
    setFiles((cur) => cur.filter((f) => f.id !== id));
  }, []);

  const onRetry = useCallback(
    (id: string) => {
      const target = files.find((f) => f.id === id);
      if (!target) return;
      // Re-validate BEFORE re-uploading: a validation-error row (oversize /
      // unsupported) must keep its error, never attempt the invalid file.
      const error = validate(target.file);
      if (error) {
        setFiles((cur) =>
          cur.map((f) =>
            f.id === id ? { ...f, status: 'error', error, progress: 0 } : f
          )
        );
        return;
      }
      void runUpload(id, target.file);
    },
    [files, validate, runUpload]
  );

  const clear = useCallback(() => {
    controllersRef.current.forEach((c) => c.abort());
    controllersRef.current.clear();
    setFiles([]);
  }, []);

  // Abort any in-flight uploads on unmount. Uploads only ever start from a user
  // event, so StrictMode's double-mount can't spuriously kick one off; a real
  // unmount aborting is correct and lets the caller's `upload` roll back.
  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      controllers.forEach((c) => c.abort());
      controllers.clear();
    };
  }, []);

  return { files, onFilesAdded, onRemove, onRetry, clear };
}
